/* REACT PACKAGE IMPORTS */
import { useEffect, useState, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { Button } from "react-bootstrap";

/* MUI PACKAGE IMPORTS */
import { IconButton, Tooltip, Menu, MenuItem } from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import CalculateIcon from "@mui/icons-material/Calculate";
import TranslateIcon from "@mui/icons-material/Translate";

/* INTERNAL IMPORTS */
import { flattenArr } from "../../Utilities/tools.js";
import CountdownTimer from "../Timer";
import CustomModal from "../Modal";
import ModalQuestionsPreview from "../ModalQuestionsPreview";
import Calculator from "../Calculator/Calculator";
import SessionPages from "./SessionPages";
import SessionDetails from "./SessionDetails";

import { endSessionHandle } from "../../Features/Sessions/Actions/SessionsActions";

export default function SessionFixedBar({ sessionState, sessionStatus }) {
  const mounted = useRef(null);
  const studyMaterialsArray = [
    {
      link: "https://digitalhub.fifa.com/m/1c21b25b00c6dec8/original/FIFA-Football-Agent-Exam-Study-Materials.pdf",
      title: "Study Materials March 26 Edition (EN)",
    },
    {
      link: "https://digitalhub.fifa.com/m/1009119579a8c8c4/original/Materiales-de-estudio-sobre-el-examen-de-la-FIFA-para-agentes-de-futbol.pdf",
      title: "Study Materials March 26 Edition (ES)",
    },
    {
      link: "https://digitalhub.fifa.com/m/6461f8f0ff0ee7d1/original/Materiel-pedagogique-de-l-examen-de-la-FIFA-pour-les-agents.pdf",
      title: "Study Materials March 26 Edition (FR)",
    },
  ];

  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showCalculator, setShowCalculator] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [modalMessage, setModalMessage] = useState("");
  const [confirmationWarnings, setConfirmationWarnings] = useState([]);
  const [expired, setExpired] = useState(true);
  const [timeLeft, setTimeLeft] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const { questions } = useSelector((store) => store.assessments);
  const { session, answersSaved } = useSelector((store) => store.sessions);
  const { timeleft, schedule } = session;
  const { exam } = schedule;
  const { duration } = exam;

  const { id } = useParams();
  const sessionId = parseInt(id);
  const dispatch = useDispatch();

  const handleSessionExpired = useCallback((expired) => {
    setExpired(expired);
  }, []);

  const getTotalQuestions = () => {
    let questionsArray = questions.map((question) => question.questions);
    let mergedQuestions = [].concat
      .apply([], questionsArray)
      .map((question) => question.questions);
    let totalQuestions = flattenArr(mergedQuestions);
    return totalQuestions;
  };

  const formatDate = (date) => {
    const { hours, minutes, seconds } = date;
    const nowInMs = new Date().getTime();
    const timeAfterSession = (hours * 60 * 60 + minutes * 60 + seconds) * 1000;
    return nowInMs + timeAfterSession;
  };

  const idleStatus = ["future", "finished", "pending", "expired"];

  useEffect(() => {
    mounted.current = true;
    if (!mounted.current) return;

    if (idleStatus.includes(sessionStatus)) return;
    if (timeleft) {
      setTimeLeft(formatDate(timeleft));
      setExpired(false);
    } else if (duration) {
      setTimeLeft(formatDate(duration));
      setExpired(true);
    }
    return () => (mounted.current = false);
  }, [sessionStatus, timeleft]);

  // HANDLER FUNCTIONS
  const handleOpenTranslator = () => {
    const url = "https://www.deepl.com/en/translator";
    const windowFeatures =
      "width=800,height=600,toolbar=no,menubar=no,location=no,resizable=yes,scrollbars=yes";
    window.open(url, "DeepLTranslatePopup", windowFeatures);
  };

  const handleConfirmFinish = () => {
    setShowConfirmationModal(true);
    const warningsArray = [];
    const totalQuestions = getTotalQuestions();
    if (!expired) {
      warningsArray.push(
        "There is still some time left until the end of the session.",
      );
    }

    if (answersSaved.length < totalQuestions.length) {
      warningsArray.push("There are still some unanswered questions.");
    }
    warningsArray.push(
      `You are answered ${answersSaved.length} of ${totalQuestions.length} questions`,
    );

    setModalTitle("Finish Assessment");
    setModalMessage("Are you sure you want to finish this test?");
    setConfirmationWarnings(warningsArray);
  };

  const handleFinish = (e, i) => {
    dispatch(endSessionHandle(sessionId)).then(() => {
      setShowConfirmationModal(false);
    });
  };

  return (
    <div className="box bg-white w-full h-min-20 fixed bottom-0 left-0 p-2">
      <div className="container flex justify-between items-center gap-4">
        <SessionDetails
          sessionId={sessionId}
          sessionState={sessionState}
          sessionStatus={sessionStatus}
        />
        <SessionPages />
        <div className="w-full">
          <CountdownTimer
            sessionStatus={"finished"}
            targetDate={timeLeft}
            sessionExpired={expired || idleStatus.includes(sessionStatus)}
            setSessionExpired={handleSessionExpired}
            paused={false}
          />
        </div>
        <div className="border-1 border-black rounded-full">
          <Tooltip title="Access the Calculator tool in a sidebar">
            <IconButton
              aria-label="calculator"
              color="inherit"
              onClick={() => setShowCalculator(!showCalculator)}
            >
              <CalculateIcon sx={{ fontSize: "24px" }} />
            </IconButton>
          </Tooltip>
        </div>
        <div className="border-1 border-black rounded-full">
          <Tooltip title="This link will open the unofficial translator tool in a new window. Please use it at your own discretion and risk.">
            <IconButton
              aria-label="notebook"
              color="inherit"
              onClick={handleOpenTranslator}
            >
              <TranslateIcon sx={{ fontSize: "24px" }} />
            </IconButton>
          </Tooltip>
        </div>
        <div className="border-1 border-black rounded-full">
          <Tooltip title="The following links will open the Study Materials in a new window.">
            <IconButton
              aria-label="notebook"
              color="inherit"
              onClick={handleClick}
            >
              <InfoIcon sx={{ fontSize: "24px" }} />
            </IconButton>
          </Tooltip>
          <Menu
            id="basic-menu"
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            MenuListProps={{
              "aria-labelledby": "basic-button",
            }}
          >
            {studyMaterialsArray.map(({ link, title }, i) => {
              return (
                <MenuItem key={i}>
                  <a
                    className="!text-black hover:!text-gray-500 no-underline"
                    href={link}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {title}
                  </a>
                </MenuItem>
              );
            })}
          </Menu>
        </div>
        <Button
          className="text-white px-4 py-2 inter"
          variant="dark"
          onClick={handleConfirmFinish}
          disabled={idleStatus.includes(sessionStatus)}
        >
          Finish
        </Button>
      </div>
      <CustomModal
        setShowModal={setShowConfirmationModal}
        setShowPreviewModal={setShowPreviewModal}
        show={showConfirmationModal}
        handleSubmit={handleFinish}
        motives={confirmationWarnings}
        msg={modalMessage}
        title={modalTitle}
        button="Finish Assessment"
      />
      <ModalQuestionsPreview
        setShowModal={setShowPreviewModal}
        show={showPreviewModal}
        // handleSubmit={handleFinish}
        // motives={confirmationWarnings}
        // msg={modalMessage}
        // title={modalTitle}
        // button="Finish Assessment"
      />
      <Calculator show={showCalculator} setShow={setShowCalculator} />
    </div>
  );
}
